import React, { useState, useCallback, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress,
    Alert,
    Divider,
    Paper
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';
import { apiHandler } from '../../../utils/api/responseHandler';
import config from '../../../config';
import logger from '../../../utils/logging/logger';
import { useAuth } from '../../../hooks/auth/useAuth';
import { parseSelectionSummary } from '../../../utils/formatSelectionSummary';
import type { QuoteData } from '../../../types/quoter/types';
import { usePricing } from '../../../hooks/quoter/usePricing';
import PricingDisplay from '../../shared/PricingDisplay';

const componentName = 'CartSummaryPrintModal';

interface ContentBlock {
    ContentHeader: string;
    ContentMain: string;
}

interface Boat {
    BoatId: number;
    BoatModel: string;
    I: number | null;
    J: number | null;
    P: number | null;
    E: number | null;
    BoatLengthFt: number;
}

interface CartItemDetails {
    SailName?: string;
    BoatModel?: string;
    AdjustedSquareFeet?: number;
    ClothMakeName?: string;
    ClothDisplayName?: string;
    ClothOverrideGradeId?: number;
    OverrideClothId?: number;
    DefaultClothId?: number;
    IsEverydaySail?: number;
    FullBattens?: number;
    WindAverageAnswerId?: number;
    SailUseAnswerId?: number;
    SailWeightTypeId?: number;
    OptionPrice?: number;
    Notes?: string;
    SailDescription?: string;
    SelectionSummary?: string; // From f_Order_QuoteSelectionSummary

    // Pricing fields from updated API
    CalculatedPrice?: number;
    CustomerDiscountPercent?: number;
    CustomerDiscountApplied?: number;
    ResaleDiscountPercent?: number;
    ResaleDiscountApplied?: number;
    ResaleUserPrice?: number;
    QuotedPrice?: number;

    // Additional fields for QuoteData compatibility
    BoatId?: number;
    SailId?: number;
    SailCutId?: number;
    IsFurling?: number;
    PercentLP?: number;
    SailDisplayName?: string;
}

interface CartItem {
    Order_QuoteId: number;
    OrderId: number;
    details: CartItemDetails;
    options: any[];
    quantity: number;
}

interface CartSummaryPrintModalProps {
    open: boolean;
    onClose: () => void;
    cartItems: CartItem[];
    shippingCost: number;
    subtotal: number;
    total: number;
    showResellerPricing?: boolean; // Set to true for reseller/admin users to show dual pricing buttons
}

// Separate component for individual cart items (can use hooks)
const CartItemDisplay: React.FC<{
    item: CartItem;
    hideResellerPricing: boolean;
}> = ({ item, hideResellerPricing }) => {
    const [boatData, setBoatData] = useState<Boat | null>(null);
    const [standardFeatures, setStandardFeatures] = useState<string>('');
    const [loadingBoatData, setLoadingBoatData] = useState<boolean>(false);

    // Format measurement helper
    const formatMeasurement = (value: number | null | undefined): string => {
        if (typeof value === 'number') {
            return value.toFixed(2);
        }
        return 'N/A';
    };

    // Fetch boat data and standard features
    useEffect(() => {
        const fetchItemData = async () => {
            if (!item.details?.BoatId) return;
            
            setLoadingBoatData(true);
            try {
                // Fetch boat data
                const boatResponse = await apiHandler.fetch<Boat>(
                    `${config.API_URL}/api/boat/${item.details.BoatId}`
                );
                if (!boatResponse.error && boatResponse.data) {
                    setBoatData(boatResponse.data);
                }

                // Fetch standard features if we have the required IDs
                if (item.details?.SailId) {
                    const featuresResponse = await apiHandler.fetch<ContentBlock[]>(
                        `${config.API_URL}/api/content?contentCategoryCode=SAIL_STD_FEATURES&sailId=${item.details.SailId}&sailCutId=${item.details?.SailCutId || 0}&isFurling=${item.details?.IsFurling || 0}`
                    );
                    if (!featuresResponse.error && featuresResponse.data?.[0]) {
                        setStandardFeatures(featuresResponse.data[0].ContentMain || '');
                    }
                }
            } catch (err) {
                logger.log('LogError', componentName, 'Error fetching item data:', err);
            } finally {
                setLoadingBoatData(false);
            }
        };

        fetchItemData();
    }, [item.details?.BoatId, item.details?.SailId, item.details?.SailCutId, item.details?.IsFurling]);

    // Create customer-visible pricing data with all required QuoteData fields
    const createDisplayData = (): QuoteData => {
        const baseData = {
            ...item.details,
            // Add all missing required QuoteData fields with defaults
            QuoteLeadDays: 0,
            CreateTime: new Date().toISOString(),
            Sail_ImgName: '',
            UserId: 0,
            SailWeightName: '',
            SelectedClothId: 0,
            DiscountCodeId: 0,
            PromoCodeId: 0,
            PromoDiscountPercent: 0,
            PromoDiscountApplied: 0,
            SelectionSummary: null,
        };

        if (hideResellerPricing) {
            // Customer view - hide reseller discounts
            return {
                ...baseData,
                ResaleDiscountPercent: 0,
                ResaleDiscountApplied: 0,
                ResaleUserPrice: 0,
            } as QuoteData;
        }

        // Admin or regular customer - show full pricing
        return baseData as QuoteData;
    };

    const displayData = createDisplayData();
    const pricing = usePricing(displayData); // Hook called in React component - OK

    // Format sail options for display
    const formatSailOptions = (options: any[]) => {
        // Use SelectionSummary from database if available (preferred - single source of truth)
        if (item.details?.SelectionSummary) {
            return parseSelectionSummary(item.details.SelectionSummary).map(item => item.text);
        }

        // Fallback to manual processing if SelectionSummary not available
        if (!options || options.length === 0) {
            return [];
        }

        return options
            .filter(option => option.ValueSelected > 0)
            .map(option => {
                const cost = option.OptionQuotedPrice !== 0 ? ` (${Math.round(option.OptionQuotedPrice)})` : '';
                
                // Special handling for reefs - always show the number
                if (option.OptionDetails && option.OptionDetails.toLowerCase().includes('reef')) {
                    return `${option.OptionDetails}: ${option.ValueSelected}${cost}`;
                }
                
                if (option.ValueSelected === 1) {
                    return `${option.OptionDetails}${cost}`;
                } else {
                    return `${option.OptionDetails}: ${option.ValueSelected}${cost}`;
                }
            });
    };

    // Get cloth name helper
    const getClothName = () => {
        if (item.details?.ClothDisplayName) {
            return item.details.ClothDisplayName;
        }
        return "Standard";
    };

    const renderItemPrice = () => {
        if (item.quantity === 1) {
            return (
                <Box sx={{ textAlign: 'right' }}>
                    <PricingDisplay
                        quoteData={displayData}
                        variant="print"
                        showDisclaimer={false}
                        viewAsCustomer={hideResellerPricing}
                    />
                </Box>
            );
        } else {
            const unitPrice = pricing?.finalPrice || displayData?.QuotedPrice || 0;
            const totalPrice = unitPrice * item.quantity;

            return (
                <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body1" color="text.secondary" gutterBottom>
                        ${Math.round(unitPrice).toLocaleString()} each
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        ${Math.round(totalPrice).toLocaleString()}
                    </Typography>
                </Box>
            );
        }
    };

    return (
        <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <Typography 
                        variant="h5" 
                        gutterBottom
                        sx={{
                            '@media print': {
                                fontSize: '1.1rem !important',
                                fontWeight: 'bold !important'
                            }
                        }}
                    >
                        {item.details?.BoatModel || 'Boat'}
                        {' - '}
                        {item.details?.SailName || 'Sail'}
                        {item.quantity > 1 && (
                            <Typography component="span" variant="body1" color="text.secondary">
                                {' '}(Qty: {item.quantity})
                            </Typography>
                        )}
                    </Typography>

                    {/* Display boat measurements (I,J,P,E) and area */}
                    {boatData && (
                        <Typography variant="body1" gutterBottom>
                            I: {formatMeasurement(boatData.I)}, J: {formatMeasurement(boatData.J)},
                            P: {formatMeasurement(boatData.P)}, E: {formatMeasurement(boatData.E)}
                            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                            {/* Only show LP% for headsails (SailId = 3) */}
                            {item.details?.SailId === 3 && (
                                <>{item.details?.PercentLP || 'N/A'}% LP, </>
                            )}
                            Area: {item.details?.AdjustedSquareFeet?.toFixed(0) || 'N/A'} sq. ft.
                        </Typography>
                    )}
                    
                    {!boatData && (
                        <Typography variant="body1" gutterBottom>
                            <strong>Area:</strong> {item.details?.AdjustedSquareFeet || 0} sq. ft.
                        </Typography>
                    )}

                    <Typography variant="body1" gutterBottom>
                        <strong>Cloth:</strong> {getClothName()}
                    </Typography>

                    {/* Standard Features - only show in customer view */}
                    {hideResellerPricing && standardFeatures && (
                        <Box sx={{ mt: 1, mb: 1 }}>
                            <Typography variant="body1" gutterBottom>
                                <strong>Standard Features:</strong>
                            </Typography>
                            <Typography variant="body1" sx={{ ml: 2, mb: 1 }}>
                                {standardFeatures}
                            </Typography>
                        </Box>
                    )}

                    {/* Display formatted options */}
                    {formatSailOptions(item.options).length > 0 && (
                        <Box sx={{ mt: 0.5, mb: 0.5 }}>
                            <Typography variant="body1" gutterBottom>
                                <strong>Options:</strong>
                            </Typography>
                            {formatSailOptions(item.options).map((option, optionIndex) => (
                                <Typography
                                    key={optionIndex}
                                    variant="body1"
                                    sx={{ ml: 2, mb: 0.25 }}
                                >
                                    - {option}
                                </Typography>
                            ))}
                        </Box>
                    )}

                    {item.details?.Notes && (
                        <Box sx={{ mt: 0.5 }}>
                            <Typography variant="body1" gutterBottom>
                                <strong>Notes:</strong> {item.details.Notes}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Item Pricing */}
                <Box sx={{ ml: 3, minWidth: '150px' }}>
                    {renderItemPrice()}
                </Box>
            </Box>
        </Box>
    );
};

const CartSummaryPrintModal: React.FC<CartSummaryPrintModalProps> = ({
    open,
    onClose,
    cartItems,
    shippingCost,
    subtotal,
    total,
    showResellerPricing = false
}) => {
    const [footerContent, setFooterContent] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [currentView, setCurrentView] = useState<'customer' | 'reseller'>('customer');
    const { user } = useAuth();

    // Control reseller pricing display based on prop and current view
    const hideResellerPricing = !showResellerPricing || currentView === 'customer';

    type OrderSummary = {
        SubTotal_CustomerPrice: number;
        SubTotal_CustomerDiscount: number;
        Total_CustomerQuotedPrice: number;
        SubTotal_ResalePrice: number;
        SubTotal_ResaleDiscount: number;
        Total_ResalePrice: number;
        ShippingEstimate: number;
        Taxes: number;
        CartTotal_Customer: number;
        CartTotal_Reseller: number;
        UpdateTime: string;
        CreateTime: string;
    };

    const [orderSummary, setOrderSummary] = useState<OrderSummary | null>(null);
    const [orderData, setOrderData] = useState<{ quoteCode?: string } | null>(null);

    const fetchOrderSummary = useCallback(async () => {
        try {
            const orderId = cartItems?.[0]?.OrderId;
            if (!orderId) {
                setOrderSummary(null);
                return;
            }

            const resp = await apiHandler.fetch<{ success?: boolean; summary?: OrderSummary }>(
                `${config.API_URL}/api/order/${orderId}/summary`,
                { validateFn: (d) => d as any }
            );
            if (!resp.error && resp.data?.summary) {
                setOrderSummary(resp.data.summary);
            } else {
                setOrderSummary(null);
            }
        } catch (e) {
            // Log as warning since 404 is expected when order is empty/deleted
            logger.log('LogWarning', componentName, 'Order summary not available in print modal', e);
            setOrderSummary(null);
        }
    }, [cartItems]);

    const fetchOrderData = useCallback(async () => {
        try {
            const orderId = cartItems?.[0]?.OrderId;
            if (!orderId || !user?.userId || !user?.userTypeId) {
                setOrderData(null);
                return;
            }

            // Use the order summary API which now includes QuoteCode
            const response = await fetch(`${config.API_URL}/api/order/${orderId}/summary`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'X-User-Id': user.userId.toString(),
                    'X-User-Type-Id': user.userTypeId.toString()
                }
            });
            
            if (response.ok) {
                const result = await response.json();
                
                // Extract quoteCode from the summary response
                const quoteCode = result.summary?.QuoteCode || result.summary?.quoteCode || result.QuoteCode || result.quoteCode;
                if (quoteCode) {
                    setOrderData({ quoteCode: quoteCode });
                    return;
                }
            }
            
            // If summary API doesn't have quoteCode, set to null
            setOrderData(null);
        } catch (e) {
            // Silently handle errors - quote code is optional
            logger.log('LogWarning', componentName, 'Quote code not available', e);
            setOrderData(null);
        }
    }, [cartItems, user?.userId, user?.userTypeId]);

    // Calculate customer pricing for display
    //const displaySubtotal = hideResellerPricing && orderSummary
    //    ? Number(orderSummary.Total_CustomerQuotedPrice || 0)
    //    : subtotal;
    const displaySubtotal = subtotal;

    const displayTotal = hideResellerPricing && orderSummary
        ? Number(orderSummary.CartTotal_Customer || 0)
        : total;

    const displayTaxes = orderSummary ? Number(orderSummary.Taxes || 0) : 0;

    const getCurrentDate = () => {
        return new Date().toLocaleDateString('en-US', {
            timeZone: 'America/Los_Angeles',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const generateQuoteCode = () => {
        if (!user || !orderData?.quoteCode) {
            return '-';
        }

        const quoteCode = orderData.quoteCode;
        return `${quoteCode}`;
    };

    const fetchFooterContent = useCallback(async () => {
        if (!open) return;

        setIsLoading(true);
        setError(null);

        try {
            const footerResponse = await apiHandler.fetch<ContentBlock[]>(
                `${config.API_URL}/api/content?contentCategoryCode=QUOTE_PRINTING&contentCode=TIME_LIMIT`,
                {
                    validateFn: (data) => data,
                }
            );

            if (footerResponse.error) {
                logger.log('LogError', componentName, 'Error fetching footer content:', footerResponse.error);
            } else {
                setFooterContent(footerResponse.data?.[0]?.ContentMain || '');
            }
        } catch (err) {
            logger.log('LogError', componentName, 'Error fetching footer content:', err);
            setError('Failed to load footer content');
        } finally {
            setIsLoading(false);
        }
    }, [open]);

    useEffect(() => {
        if (open) {
            // Reset to customer view when modal opens
            setCurrentView('customer');
            fetchFooterContent();
            fetchOrderSummary();
            fetchOrderData();
        }
    }, [open, fetchFooterContent, fetchOrderSummary, fetchOrderData]);

    const handlePrint = () => {
        window.print();
    };

    const handlePrintCustomerView = () => {
        setCurrentView('customer');
        // Use setTimeout to ensure state update is applied before printing
        setTimeout(() => {
            window.print();
        }, 100);
    };

    const handlePrintResellerView = () => {
        setCurrentView('reseller');
        // Use setTimeout to ensure state update is applied before printing
        setTimeout(() => {
            window.print();
        }, 100);
    };

    if (!open) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            className="print-modal"
            sx={{
                '& .MuiDialog-paper': {
                    width: '90%',
                    maxWidth: '800px',
                    height: '90vh',
                    '@media print': {
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: 'auto',
                        maxWidth: 'none',
                        maxHeight: 'none',
                        margin: 0,
                        padding: 0,
                        boxShadow: 'none',
                        overflow: 'visible',
                        border: 'none',
                        fontSize: '0.85rem'
                    }
                },
                '@media print': {
                    // Hide the backdrop
                    '& .MuiBackdrop-root': {
                        display: 'none'
                    },
                    // Position the container properly for print
                    '& .MuiDialog-container': {
                        position: 'static',
                        height: 'auto',
                        overflow: 'visible',
                        transform: 'none'
                    }
                }
            }}
        >
            <DialogTitle className="no-print" sx={{ '@media print': { display: 'none' } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">
                        Cart Summary
                    </Typography>
                    {showResellerPricing && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                size="small"
                                variant={currentView === 'customer' ? 'contained' : 'outlined'}
                                onClick={() => setCurrentView('customer')}
                            >
                                Customer View
                            </Button>
                            <Button
                                size="small"
                                variant={currentView === 'reseller' ? 'contained' : 'outlined'}
                                onClick={() => setCurrentView('reseller')}
                            >
                                Reseller View
                            </Button>
                        </Box>
                    )}
                </Box>
            </DialogTitle>

            <DialogContent
                className="print-content"
                sx={{
                    '@media print': {
                        position: 'static',
                        padding: 0,
                        overflow: 'visible',
                        height: 'auto',
                        maxHeight: 'none'
                    }
                }}
            >
                {isLoading && (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
                        <CircularProgress />
                    </Box>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {!isLoading && (
                    <Paper
                        elevation={0}
                        className="printable-content"
                        sx={{
                            p: 3,
                            '@media print': {
                                boxShadow: 'none',
                                border: 'none',
                                p: 2,
                                margin: 0,
                                // Override all Typography variants for print, except those with specific overrides
                                '& .MuiTypography-root': {
                                    fontSize: '0.85em'
                                },
                                // Control page breaks
                                pageBreakInside: 'auto',
                                pageBreakAfter: 'avoid'
                            }
                        }}
                    >
                        {/* Header with Logo */}
                        <Box sx={{
                            textAlign: 'center',
                            mb: 3,
                            '@media print': {
                                pageBreakInside: 'avoid',
                                pageBreakAfter: 'auto'
                            }
                        }}>
                            <img
                                src="/images/point-sailsLogo.svg"
                                alt="Point Sails"
                                style={{
                                    maxHeight: '80px',
                                    marginBottom: '16px'
                                }}
                            />
                            <Typography 
                                variant="h4" 
                                gutterBottom
                                sx={{
                                    '@media print': {
                                        fontSize: '1.6rem !important',
                                        fontWeight: 'bold !important'
                                    }
                                }}
                            >
                                Cart Summary
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                {getCurrentDate()}
                            </Typography>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {/* Cart Items */}
                        {cartItems.map((item, index) => (
                            <Box key={`${item.Order_QuoteId}-${index}`}
                                sx={{
                                    '@media print': {
                                        pageBreakInside: 'avoid'
                                    }
                                }}
                            >
                                <CartItemDisplay
                                    item={item}
                                    hideResellerPricing={hideResellerPricing}
                                />
                                {index < cartItems.length - 1 && <Divider sx={{ my: 2 }} />}
                            </Box>
                        ))}

                        <Divider sx={{ my: 3 }} />

                        {/* Pricing Summary */}
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            mt: 3,
                            '@media print': {
                                pageBreakInside: 'avoid',
                                pageBreakBefore: 'auto'
                            }
                        }}>
                            <Box sx={{ width: '100%' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Quote Code: {generateQuoteCode()}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <Typography variant="h5">
                                            <strong>Total:</strong>
                                        </Typography>
                                        <Typography variant="h5">
                                            <strong>${Math.round(displayTotal).toLocaleString()}</strong>
                                        </Typography>
                                    </Box>
                                </Box>

                                {displayTaxes > 0 ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'right', pr: 2 }}>
                                            Taxes:
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            ${Math.round(displayTaxes).toLocaleString()}
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            + Shipping and Applicable Taxes
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                *All prices displayed are estimates and subject to review.
                            </Typography>
                            {/* Footer Content - Quote validity message */}
                            {footerContent && (
                                <Typography sx={{ mt: 0.5 }} variant="body2" color="text.secondary">
                                    {footerContent}
                                </Typography>
                            )}
                        </Box>
                    </Paper>
                )}
            </DialogContent>

            <DialogActions className="no-print" sx={{ '@media print': { display: 'none' } }}>
                {showResellerPricing ? (
                    // Show dual print buttons for reseller/admin users
                    <>
                        <Button 
                            onClick={handlePrintCustomerView} 
                            startIcon={<PrintIcon />} 
                            variant="outlined"
                            color={currentView === 'customer' ? 'primary' : 'inherit'}
                        >
                            Print Customer View
                        </Button>
                        <Button 
                            onClick={handlePrintResellerView} 
                            startIcon={<PrintIcon />} 
                            variant="outlined"
                            color={currentView === 'reseller' ? 'primary' : 'inherit'}
                        >
                            Print Reseller View
                        </Button>
                    </>
                ) : (
                    // Show single print button for regular users
                    <Button onClick={handlePrint} startIcon={<PrintIcon />} variant="outlined">
                        Print
                    </Button>
                )}
                <Button onClick={onClose} startIcon={<CloseIcon />}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CartSummaryPrintModal;